import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Select } from '@shared/ui/Input';
import { Loader } from '@shared/ui/Progress';
import { useAppSelector } from '@app/hooks';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { lawsuitStatusLabel, lawsuitStatusTone } from '@shared/lib/statusLabels';
import {
  useGetLawsuitQuery,
  useComposeLawsuitMutation,
  useDeleteLawsuitMutation,
  useDeleteComposedMutation,
  useListStatutProjectsQuery,
  useSetLawsuitProjectMutation,
} from '@shared/api';
import { lawsuitClaimLabel, lawsuitContractLabel } from '@entities/legal/types';
import { useAgentPermission } from '@entities/user';

/**
 * Карточка иска (Lawsuit). Открывается по `/agents/statut/lawsuits/:id`.
 *
 * Состояния по `lawsuit.status`:
 *   - 'draft'      — иск создан, но compose не запускался (редкий случай:
 *                    юрист зашёл до того, как первый compose стартанул).
 *                    Показываем кнопку «Составить иск».
 *   - 'composing'  — Worker считает. Лоадер «Составляем…», блокируем
 *                    действия. WS-event `lawsuit_composed` /
 *                    `lawsuit_failed` инвалидирует тег и перерисует.
 *   - 'ready'      — DOCX готов. Берём `composed[0].id` (отсортирован по
 *                    created_at DESC на бэке) — кнопка «Скачать DOCX»,
 *                    «Удалить черновик» (повторно можно перегенерировать).
 *   - 'failed'     — error_message + кнопка «Перегенерировать».
 *
 * Связки:
 *   - Исходная претензия — ссылка на ClaimCard, если claim_id не NULL;
 *     иначе подпись с именем загруженного файла.
 *   - Договор — ссылка на ContractCard, либо имя файла, либо «—».
 */

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(5)};

  & > h1 {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    letter-spacing: -0.01em;
    flex: 1;
    min-width: 0;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Sections = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Section = styled.section`
  & > h2 {
    margin: 0 0 ${({ theme }) => theme.spacing(3)};
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const RowLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.radius.xs};
  }
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 13px;
`;

const FileLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};

  & > span:first-child {
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const ComposingBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.status.info}10;
  border: 1px solid ${({ theme }) => theme.colors.status.info}33;
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid ${({ theme }) => theme.colors.status.info}55;
  border-top-color: ${({ theme }) => theme.colors.status.info};
  border-radius: 50%;
  animation: ${spin} 800ms linear infinite;
  flex-shrink: 0;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    border-top-color: ${({ theme }) => theme.colors.status.info};
    border-style: solid;
  }
`;

const FailedBox = styled.div`
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.status.danger}10;
  border-left: 3px solid ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  line-height: 1.55;

  & > strong {
    color: ${({ theme }) => theme.colors.status.danger};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const DangerZone = styled.div`
  margin-top: ${({ theme }) => theme.spacing(6)};
  padding-top: ${({ theme }) => theme.spacing(4)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: flex-end;
`;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  lawsuitId: string;
  onClose: () => void;
}

export function LawsuitCard({ lawsuitId, onClose }: Props) {
  const navigate = useNavigate();
  const { can_write } = useAgentPermission('statut');
  const { data, isLoading } = useGetLawsuitQuery(lawsuitId);
  const { data: projectsData } = useListStatutProjectsQuery();
  const [setLawsuitProject, { isLoading: isSettingProject }] = useSetLawsuitProjectMutation();
  const [compose, { isLoading: isQueuing }] = useComposeLawsuitMutation();
  const [deleteLawsuit, { isLoading: isDeleting }] = useDeleteLawsuitMutation();
  const [deleteComposed] = useDeleteComposedMutation();
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (isLoading || !data) {
    return <Loader />;
  }

  const { lawsuit, composed } = data;
  const lawsuitProjectId = (lawsuit as { project_id?: string | null }).project_id ?? '';
  const statutProjects = projectsData?.items ?? [];
  const latestComposed = composed?.[0] ?? null;

  async function onDownload(composedId: string) {
    setActionError(null);
    setDownloadingId(composedId);
    try {
      const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
      const res = await downloadAuthorizedFile(
        `${base}/agents/statut/composed/${composedId}/download`,
        { accessToken, fallbackName: `${lawsuit.title || 'Иск'}.docx` },
      );
      if (!res.ok) {
        setActionError(`Не удалось скачать DOCX (${res.error ?? `HTTP ${res.status}`}).`);
      }
    } finally {
      setDownloadingId(null);
    }
  }

  async function onDeleteComposed(composedId: string) {
    if (!window.confirm('Удалить текущий черновик иска? Можно будет перегенерировать.')) {
      return;
    }
    setActionError(null);
    try {
      await deleteComposed(composedId).unwrap();
    } catch {
      setActionError('Не удалось удалить черновик. Попробуйте ещё раз.');
    }
  }

  async function onRecompose() {
    setActionError(null);
    try {
      await compose(lawsuitId).unwrap();
    } catch (err) {
      const detail =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        'Не удалось поставить иск в очередь. Попробуйте ещё раз.';
      setActionError(detail);
    }
  }

  async function onDeleteLawsuit() {
    if (
      !window.confirm(
        `Удалить иск «${lawsuit.title}»? Все сгенерированные черновики будут стёрты.`,
      )
    ) {
      return;
    }
    try {
      await deleteLawsuit(lawsuitId).unwrap();
      onClose();
      navigate('/agents/statut/claims?tab=lawsuits');
    } catch {
      setActionError('Не удалось удалить иск. Попробуйте ещё раз.');
    }
  }

  return (
    <Card>
      <Header>
        <h1>{lawsuit.title}</h1>
        <Badge $tone={lawsuitStatusTone(lawsuit.status)}>
          {lawsuitStatusLabel(lawsuit.status)}
        </Badge>
      </Header>

      <StatusRow>
        <Muted>Создан: {formatDate(lawsuit.created_at)}</Muted>
        {lawsuit.updated_at !== lawsuit.created_at && (
          <Muted>· Обновлён: {formatDate(lawsuit.updated_at)}</Muted>
        )}
      </StatusRow>

      <Sections>
        <Section>
          <h2>Исходная претензия</h2>
          {lawsuit.claim_id ? (
            <RowLink to={`/agents/statut/claims/${lawsuit.claim_id}`}>
              {lawsuitClaimLabel(lawsuit)}
            </RowLink>
          ) : lawsuit.manual_claim_filename ? (
            <FileLine>
              <span>Файл:</span>
              <strong>{lawsuit.manual_claim_filename}</strong>
            </FileLine>
          ) : (
            <Muted>
              Источник недоступен — исходная претензия могла быть удалена.
            </Muted>
          )}
        </Section>

        <Section>
          <h2>Договор</h2>
          {lawsuit.contract_id ? (
            <RowLink to={`/agents/statut/contracts/${lawsuit.contract_id}`}>
              {lawsuitContractLabel(lawsuit)}
            </RowLink>
          ) : lawsuit.manual_contract_filename ? (
            <FileLine>
              <span>Файл:</span>
              <strong>{lawsuit.manual_contract_filename}</strong>
            </FileLine>
          ) : (
            <Muted>Иск составлен без привязки к договору.</Muted>
          )}
        </Section>

        <Section>
          <h2>Строительный объект (ПТО)</h2>
          <Select
            value={lawsuitProjectId}
            disabled={!can_write || isSettingProject}
            onChange={(e) => {
              const pid = e.target.value || null;
              void setLawsuitProject({ lawsuitId, project_id: pid });
            }}
            style={{ maxWidth: 360 }}
            aria-label="Строительный объект судебного иска"
          >
            <option value="">— не привязан —</option>
            {statutProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code ? `${p.code} · ` : ''}
                {p.name}
              </option>
            ))}
          </Select>
        </Section>

        <Section>
          <h2>Сгенерированный иск</h2>

          {lawsuit.status === 'composing' && (
            <ComposingBox>
              <Spinner aria-hidden="true" />
              <span>
                Составляем иск… Обычно занимает 30–60 секунд. Карточка
                обновится автоматически.
              </span>
            </ComposingBox>
          )}

          {lawsuit.status === 'failed' && (
            <>
              <FailedBox role="alert">
                <strong>Не удалось составить иск.</strong>
                <br />
                {lawsuit.error_message ?? 'Подробности не сохранены.'}
              </FailedBox>
              <Actions>
                <Button
                  type="button"
                  onClick={onRecompose}
                  disabled={isQueuing || !can_write}
                  title={!can_write ? 'Только чтение' : undefined}
                >
                  {isQueuing ? 'Ставим в очередь…' : 'Перегенерировать'}
                </Button>
              </Actions>
            </>
          )}

          {lawsuit.status === 'draft' && (
            <>
              <Muted>
                Иск создан, но сборка ещё не запускалась. Запустите вручную —
                Worker подберёт шаблон, заполнит фактуру и пришлёт готовый DOCX.
              </Muted>
              <Actions>
                <Button
                  type="button"
                  onClick={onRecompose}
                  disabled={isQueuing || !can_write}
                  title={!can_write ? 'Только чтение' : undefined}
                >
                  {isQueuing ? 'Ставим в очередь…' : 'Составить иск'}
                </Button>
              </Actions>
            </>
          )}

          {lawsuit.status === 'ready' && latestComposed && (
            <Actions>
              <Button
                type="button"
                onClick={() => onDownload(latestComposed.id)}
                disabled={downloadingId === latestComposed.id}
              >
                {downloadingId === latestComposed.id ? 'Готовим…' : 'Скачать DOCX'}
              </Button>
              {/* Страница сгенерированного DOCX: метаданные + внешний аудит
                  (текст аудита или эталонный иск от другой нейронки). */}
              <RowLink to={`/agents/statut/documents/${latestComposed.id}`}>
                Страница документа
              </RowLink>
              <Button
                type="button"
                $variant="secondary"
                onClick={() => onDeleteComposed(latestComposed.id)}
                disabled={!can_write}
                title={!can_write ? 'Только чтение' : undefined}
              >
                Удалить черновик
              </Button>
              <Button
                type="button"
                $variant="secondary"
                onClick={onRecompose}
                disabled={isQueuing || !can_write}
                title={!can_write ? 'Только чтение' : undefined}
              >
                {isQueuing ? 'Ставим в очередь…' : 'Перегенерировать'}
              </Button>
            </Actions>
          )}

          {lawsuit.status === 'ready' && !latestComposed && (
            <Muted>Статус «Готов», но файла нет — попробуйте перегенерировать.</Muted>
          )}
        </Section>

        {actionError && (
          <FailedBox role="alert">
            <strong>Ошибка.</strong>
            <br />
            {actionError}
          </FailedBox>
        )}
      </Sections>

      <DangerZone>
        <Button
          type="button"
          $variant="secondary"
          onClick={onDeleteLawsuit}
          disabled={isDeleting || !can_write}
          title={!can_write ? 'Только чтение' : undefined}
        >
          {isDeleting ? 'Удаляем…' : 'Удалить иск'}
        </Button>
      </DangerZone>
    </Card>
  );
}
